import type { DecorationMotion, DecorationSpec } from '../types';

// v2 §D1: deterministic position + timing math for `<DecorationLayer>`.
// Two goals:
// - **Stable across renders.** No `Math.random()` — React would resample
//   on every commit and sprites would teleport. We use a low-discrepancy
//   sequence (golden-ratio increment) keyed off the sprite index.
// - **Visually scattered.** Modulo-by-1 of `i * φ` gives a far better
//   spread than `i / N` for small counts (which clusters on a grid).

const PHI = (Math.sqrt(5) - 1) / 2; // 0.6180339...
const PHI_TIMING = 0.7919; // Coprime-ish offset so timing doesn't track positions.

const DENSITY_COUNT: Record<DecorationSpec['density'], number> = {
  low: 6,
  med: 12,
  high: 20,
};

const SPEED_MULTIPLIER: Record<DecorationSpec['speed'], number> = {
  slow: 1.6,
  med: 1.0,
  fast: 0.6,
};

// Base animation duration (seconds) per motion. Tuned so each motion
// reads as the right phenomenon at speed='med': clouds drift across the
// screen in ~80s, snow falls in ~18s, twinkles pulse in 3.5s, etc.
const MOTION_BASE_DURATION_S: Record<DecorationMotion, number> = {
  drift: 80,
  fall: 18,
  'float-up': 14,
  orbit: 8,
  twinkle: 3.5,
};

// Low-discrepancy spread on [min, max] for a given index.
function spread(i: number, min: number, max: number): number {
  return min + ((i * PHI) % 1) * (max - min);
}

export function spriteCount(density: DecorationSpec['density']): number {
  return DENSITY_COUNT[density];
}

export function animationDurationS(spec: DecorationSpec): number {
  return MOTION_BASE_DURATION_S[spec.motion] * SPEED_MULTIPLIER[spec.speed];
}

// Per-sprite inline style. Position depends on motion: drift sprites
// are vertically scattered (they cross the screen horizontally), fall
// sprites are horizontally scattered (they cross vertically), orbit /
// twinkle sprites scatter in both axes.
export interface SpriteStyle {
  top?: string;
  left?: string;
  animationDuration: string;
  animationDelay: string;
}

export function spriteStyle(i: number, spec: DecorationSpec): SpriteStyle {
  const duration = animationDurationS(spec);
  // Negative delay puts each sprite at a different point in its cycle
  // already on first paint, so the screen looks "in motion" immediately.
  const delay = -((i * PHI_TIMING) % 1) * duration;

  const baseStyle = {
    animationDuration: `${duration.toFixed(2)}s`,
    animationDelay: `${delay.toFixed(2)}s`,
  };

  switch (spec.motion) {
    case 'drift':
      // Vertically scattered; CSS keyframe handles horizontal travel.
      return { ...baseStyle, top: `${spread(i, 4, 88).toFixed(2)}%` };
    case 'fall':
    case 'float-up':
      // Horizontally scattered; CSS keyframe handles vertical travel.
      return { ...baseStyle, left: `${spread(i, 2, 95).toFixed(2)}%` };
    case 'orbit':
    case 'twinkle':
      // Scattered in both axes; motion is in-place.
      return {
        ...baseStyle,
        top: `${spread(i, 6, 86).toFixed(2)}%`,
        left: `${spread(i + 1, 4, 92).toFixed(2)}%`,
      };
  }
}
